import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import Exporter, { EXCEL_EXPORT_MAX_ROW_COUNT, PDF_EXPORT_MAX_ROW_COUNT } from 'components/stateless/Exporter/Exporter';
import StatusDescriptionModal from 'components/stateless/Modal/common/status/StatusDescriptionModal';
import Pagination from 'components/stateless/Pagination/Pagination';
import SearchBorder from 'components/stateless/SearchForm/SearchBorder';
import SearchDatePicker from 'components/stateless/SearchForm/SearchDatePicker';
import SearchEmpty from 'components/stateless/SearchForm/SearchEmpty';
import SearchInput from 'components/stateless/SearchForm/SearchInput';
import SearchLabel from 'components/stateless/SearchForm/SearchLabel';
import SearchSelect from 'components/stateless/SearchForm/SearchSelect';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import Tr from 'components/stateless/Table/Tr';
import { HeaderTitle } from 'components/stateless/Title/HeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import getSelectOptions from 'constants/selectOptions';
import type { COLLATERAL_TYPE, CURRENCY_TYPE } from 'enums';
import { LOAN_STATUS } from 'enums';
import useMounted from 'hooks/useMounted';
import usePageable from 'hooks/usePageable';
import useProperty from 'hooks/useProperty';
import type Pageable from 'models/Pageable';
import type { LoanVOModel } from 'models/vo/LoanVO';
import getStatusTextClass from 'utils/classNames/getStatusTextClass';
import type { PDFExporterProps } from 'utils/exportFile/exportPDF.d';
import { setFormValues } from 'utils/form/setFormValues';
import { requestAnchorLoanList } from 'utils/http/api/anchor/loans';
import type { AnchorLoanListRequest } from 'utils/http/api/anchor/loans/requests';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { getParsedSearchParams, updateSearchParams } from 'utils/searchParams';
import type { ColumnOption, ExportSpreadSheetProps } from 'utils/spreadSheet/types';
import { getFilteredDisbursedAmount, tableValueManage } from 'utils/valueManager/ValueManager';

const AC_FINANCING_LIST_QS_KEY = 'ac-financing-list';

function AnchorFinancingList() {
  const { t } = useTranslation(['format']);
  const location = useLocation<AnchorLoanListRequest>();
  const filteredOptions = location.state;
  const mounted = useMounted();
  const { pageable, setPageable } = usePageable(AC_FINANCING_LIST_QS_KEY);
  const [financingPage, setFinancingPage] = useState<Pageable<LoanVOModel[]>>();
  const getProperty = useProperty<AnchorLoanListRequest>();
  const modal = useModal();

  const LoanStatusCheckBoxOptions: LOAN_STATUS[] = [
    LOAN_STATUS.DEALER_REQUESTED,
    LOAN_STATUS.REJECTED,
    LOAN_STATUS.APPROVED,
    LOAN_STATUS.DISBURSED,
    LOAN_STATUS.REPAID,
    LOAN_STATUS.OVERDUE,
    LOAN_STATUS.EXPIRATION,
    LOAN_STATUS.RECOURSE_ACTIVATED,
  ];

  const NUM_OF_CHECKBOX = LoanStatusCheckBoxOptions.length; // 체크박스 옵션 개수

  const { register, reset, getValues, watch, setValue, control } = useForm<AnchorLoanListRequest>();

  useEffect(() => {
    if (mounted) {
      setFormValues<AnchorLoanListRequest>(
        setValue,
        filteredOptions ? filteredOptions : getParsedSearchParams(AC_FINANCING_LIST_QS_KEY).formSearchData,
      );
      fetchAcLoanList(pageable.currentPage, pageable.sizePerPage, filteredOptions ? filteredOptions : getValues());
    }

    return () => {};
  }, [mounted]);

  const onSearchSubmit = () => {
    const data = getValues();
    fetchAcLoanList(1, pageable.sizePerPage, data);
  };

  async function fetchAcLoanList(pageNumber: number = 1, rowCount: number = 10, data: AnchorLoanListRequest) {
    try {
      const financingPage = await requestAnchorLoanList(pageNumber, rowCount, data);
      updateSearchParams(
        {
          ...data,
          pageNumber,
          rowCount,
        },
        AC_FINANCING_LIST_QS_KEY,
      );
      ReactDOM.unstable_batchedUpdates(() => {
        setFinancingPage(financingPage);
        setPageable(financingPage);
      });
    } catch (error) {
      modal.show(error);
    }
  }

  const { loanStatusConditions: watchCheckbox } = watch(); // watch for checkbox

  const paginate = async (page: number, sizePerPage: number) => {
    await fetchAcLoanList(page, sizePerPage, getParsedSearchParams(AC_FINANCING_LIST_QS_KEY).formSearchData);
  };

  const handleCheckAll = (e: any) => {
    if (e.target.checked) {
      setValue('loanStatusConditions', LoanStatusCheckBoxOptions);
    } else {
      setValue('loanStatusConditions', []);
    }
  };

  const onRemoveFilter = (event: any) => {
    reset({});
    event.preventDefault();
  };

  const onClickExportButton = async (e: any) => {
    e.preventDefault();
    const excelColumns: ColumnOption<LoanVOModel>[] = [
      {
        header: t('text:Program_Type'),
        key: 'collateralType',
      },
      {
        header: t('text:Platform_Financing_ID'),
        key: 'loanId',
      },
      {
        header: t('text:Financier_Financing_ID'),
        key: 'financierLoanId',
      },
      {
        header: t('text:Invoice_Number'),
        key: 'invoiceNumber',
      },
      {
        header: t('text:Invoice_Reference_Number'),
        key: 'referenceNumber',
      },
      {
        header: t('text:AP_Number'),
        key: 'arNumber',
      },
      {
        header: t('text:Currency'),
        key: 'currencyType',
      },
      {
        header: t('text:Partner_Name'),
        key: 'dealerName',
      },
      {
        header: t('text:Partner_Code_of_Anchor'),
        key: 'dealerCodeByAnchor',
      },
      {
        header: t('text:Requested_Financing_Amount'),
        key: 'principalAmount',

        style: {
          alignment: { horizontal: 'right' },
        },
      },
      {
        header: t('text:Financing_Amount'),
        key: 'disbursedAmount',

        style: {
          alignment: { horizontal: 'right' },
        },
      },
      {
        header: t('text:Advance_Disbursement'),
        key: 'netDisbursementAmount',

        style: {
          alignment: { horizontal: 'right' },
        },
      },
      {
        header: t('text:Applied_Date'),
        key: 'requestedDateTime',
      },
      {
        header: t('text:Approved_Date'),
        key: 'approvedDateTime',
      },
      {
        header: t('text:Disbursed_Date'),
        key: 'disbursedDate',
      },
      {
        header: t('text:Scheduled_Repayment_Date'),
        key: 'repaymentDate',
      },
      {
        header: t('text:Remaining_Financing_Term'),
        key: 'remainingFinancingTerm',

        style: {
          alignment: { horizontal: 'right' },
          numFmt: `"D"+0;"D"-0;0`,
        },
      },
      {
        header: t('text:Repaid_Date'),
        key: 'repaidDate',
      },
      {
        header: t('text:Repaid_Amount'),
        key: 'repaidAmount',
      },
      {
        header: t('text:Outstanding_Balance'),
        key: 'outstandingBalance',
      },
      {
        header: t('text:Financing_Status'),
        key: 'loanStatus',
      },
    ];

    const getPDFTableHeader = (): HeaderType[] => {
      return [
        {
          headerText: t('text:Program_Type'),
          colWidths: 100,
        },
        {
          headerText: t('text:Platform_Financing_ID'),
          colWidths: 100,
        },
        {
          headerText: t('text:Financier_Financing_ID'),
          colWidths: 100,
        },
        {
          headerText: t('text:Invoice_Number'),
          colWidths: 100,
        },
        {
          headerText: t('text:Invoice_Reference_Number'),
          colWidths: 100,
        },
        {
          headerText: t('text:AP_Number'),
          colWidths: 100,
        },
        {
          headerText: t('text:Currency'),
          colWidths: 100,
        },
        {
          headerText: t('text:Partner_Name'),
          colWidths: 140,
        },
        {
          headerText: t('text:Partner_Code_of_Anchor'),
          colWidths: 140,
        },
        {
          headerText: t('text:Requested_Financing_Amount'),
          colWidths: 120,
        },
        {
          headerText: t('text:Financing_Amount'),
          colWidths: 120,
        },
        {
          headerText: t('text:Advance_Disbursement'),
          colWidths: 120,
        },
        {
          headerText: t('text:Applied_Date'),
          colWidths: 100,
        },
        {
          headerText: t('text:Approved_Date'),
          colWidths: 100,
        },
        {
          headerText: t('text:Disbursed_Date'),
          colWidths: 100,
        },
        {
          headerText: t('text:Scheduled_Repayment_Date'),
          colWidths: 100,
        },
        {
          headerText: t('text:Repaid_Date'),
          colWidths: 100,
        },
        {
          headerText: t('text:Repaid_Amount'),
          colWidths: 120,
        },
        {
          headerText: t('text:Outstanding_Balance'),
          colWidths: 120,
        },
        {
          headerText: t('text:Financing_Status'),
          colWidths: 100,
        },
      ];
    };

    try {
      const searchData = getParsedSearchParams(AC_FINANCING_LIST_QS_KEY).formSearchData;
      const acFinancingPage = await requestAnchorLoanList(0, EXCEL_EXPORT_MAX_ROW_COUNT, searchData);
      const acFinancingPDFPage = acFinancingPage.content.slice(0, PDF_EXPORT_MAX_ROW_COUNT);

      const jsonArrayData: any[] = acFinancingPage.content.map(loanData => ({
        collateralType: tableValueManage(t(`code:collateral-type.${loanData.collateralType}`)),
        loanId: tableValueManage(loanData.loanId),
        financierLoanId: tableValueManage(loanData.financierLoanId),
        invoiceNumber: tableValueManage(loanData.invoiceNumber),
        referenceNumber: tableValueManage(loanData.referenceNumber),
        arNumber: tableValueManage(loanData.arNumber),
        currencyType: tableValueManage(loanData.currencyType),
        dealerName: tableValueManage(loanData.dealerName),
        dealerCodeByAnchor: tableValueManage(loanData.dealerCodeByAnchor),
        principalAmount: tableValueManage(loanData.principalAmount),
        disbursedAmount: tableValueManage(getFilteredDisbursedAmount(loanData.loanStatus, loanData.disbursedAmount)),
        netDisbursementAmount: tableValueManage(loanData.netDisbursementAmount),
        requestedDateTime: t('format:original-date', {
          value: loanData.requestedDateTime,
          key: 'original-date',
        }),
        approvedDateTime: t('format:original-date', {
          value: loanData.approvedDateTime,
          key: 'original-date',
        }),
        disbursedDate: t('format:original-date', {
          value: loanData.disbursedDate,
          key: 'original-date',
        }),
        repaymentDate: t('format:original-date', {
          value: loanData.repaymentDate,
          key: 'original-date',
        }),
        remainingFinancingTerm: loanData.remainingFinancingTerm,
        repaidAmount: tableValueManage(loanData.repaidPrincipalAmount),
        outstandingBalance: tableValueManage(parseFloat(loanData.outstandingBalance)),
        repaidDate: t('format:original-date', {
          value: loanData.repaidDate,
          key: 'original-date',
        }),
        loanStatus: tableValueManage(loanData.loanStatus, t(`code:financing-status.${loanData.loanStatus}`)),
      }));

      const excelExporterProps: ExportSpreadSheetProps<any> = {
        jsonArrayData: jsonArrayData,
        columns: excelColumns,
        options: {
          rowHeight: [{ position: 1, height: 30 }],
        },
      };

      const pdfExporterProps: PDFExporterProps = {
        mergedTableHeaders: [],
        tableHeaders: getPDFTableHeader(),
        tableBody: renderResultTable(acFinancingPDFPage, true),
      };

      modal.show(<Exporter spreadSheetExporterProps={excelExporterProps} pdfExporterProps={pdfExporterProps} />, {
        title: t('text:Export_File'),
        closeBtnText: t('text:Close'),
      });
    } catch (e) {
      modal.show(e);
    }
  };

  const onClickFinancingStatus = () => {
    modal.show(
      <StatusDescriptionModal
        statusDescriptionEnum={Object.values(LOAN_STATUS)}
        statusDescriptionModalType="TEXT"
        statusDescriptionEnumType="LOAN_STATUS"
      />,
      {
        modalSize: ModalSize.XL,
        modalType: ModalType.ALERT,
        closeBtnText: t('text:Close'),
      },
    );
  };

  const tableHeaders: HeaderType[] = [
    {
      headerText: t('text:Program_Type'),
      colWidths: 100,
    },
    {
      headerText: t('text:Platform_Financing_ID'),
      colWidths: 100,
    },
    {
      headerText: t('text:Financier_Financing_ID'),
      colWidths: 100,
    },
    {
      headerText: t('text:Invoice_Number'),
      colWidths: 100,
    },
    {
      headerText: t('text:Invoice_Reference_Number'),
      colWidths: 100,
    },
    {
      headerText: t('text:AP_Number'),
      colWidths: 100,
    },
    {
      headerText: t('text:Currency'),
      colWidths: 100,
    },
    {
      headerText: t('text:Partner_Name'),
      colWidths: 140,
    },
    {
      headerText: t('text:Partner_Code_of_Anchor'),
      colWidths: 140,
    },
    {
      headerText: t('text:Requested_Financing_Amount'),
      colWidths: 120,
    },
    {
      headerText: t('text:Financing_Amount'),
      colWidths: 120,
    },
    {
      headerText: t('text:Advance_Disbursement'),
      colWidths: 120,
    },
    {
      headerText: t('text:Applied_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Approved_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Disbursed_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Scheduled_Repayment_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Repaid_Date'),
      colWidths: 100,
    },
    {
      headerText: t('text:Repaid_Amount'),
      colWidths: 120,
    },
    {
      headerText: t('text:Outstanding_Balance'),
      colWidths: 120,
    },
    {
      headerText: t('text:Financing_Status'),
      colWidths: 100,
      hasStatusDescription: true,
      showStatusDescriptionFunc: onClickFinancingStatus,
    },
  ];

  const renderResultTable = (data: LoanVOModel[] | undefined, isPdf = false) => {
    return data?.map((item, i) => {
      return (
        <Tr key={i} className={isPdf ? 'virtual-table-row' : ''}>
          <Td data={t(`code:collateral-type.${item.collateralType}`)} format="code" />
          <Td data={item.loanId} />
          <Td data={item.financierLoanId} />
          <Td data={item.invoiceNumber} />
          <Td data={item.referenceNumber} />
          <Td data={item.arNumber} />
          <Td data={item.currencyType} />
          <Td data={item.dealerName} />
          <Td data={item.dealerCodeByAnchor} />
          <Td data={item.principalAmount} format="number" />
          <Td data={getFilteredDisbursedAmount(item.loanStatus, item.disbursedAmount)} format="number" />
          <Td data={item.netDisbursementAmount} format="number" />
          <Td data={item.requestedDateTime} format="date" />
          <Td data={item.approvedDateTime} format="date" />
          <Td data={item.disbursedDate} format="date" />
          <Td>
            <>
              {t('format:date', {
                value: item.repaymentDate,
                key: 'date',
              })}
              <div className="text-bold-brick-red">{item.remainingFinancingTerm}</div>
            </>
          </Td>
          <Td data={item.repaidDate} format="date" />
          <Td data={item.repaidPrincipalAmount} format="number" />
          <Td data={item.outstandingBalance} format="number" />
          <Td
            className={getStatusTextClass('LOAN_STATUS', item.loanStatus)}
            data={tableValueManage(item.loanStatus, t(`code:financing-status.${item.loanStatus}`))}
            format="code"
          />
        </Tr>
      );
    });
  };

  return (
    <>
      <HeaderTitle title={t('text:Financing')} />
      <div className="content-area">
        <form>
          <SectionTitle title={t('text:Search')}>
            <Button variant={ButtonVariantEnum.OUTLINED} color={ButtonColorEnum.SECONDARY} onClick={onRemoveFilter}>
              {t('text:Remove_Filter')}
            </Button>
          </SectionTitle>
          <SearchBorder>
            <div className="row">
              <SearchLabel label={t('text:Platform_Financing_ID')} />
              <SearchInput name={getProperty('loanId')} ref={register} />
              <SearchLabel label={t('text:Financier_Financing_ID')} />
              <SearchInput name={getProperty('financierLoanId')} ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Invoice_Number')} />
              <SearchInput name={getProperty('invoiceNumber')} ref={register} />
              <SearchLabel label={t('text:Invoice_Reference_Number')} />
              <SearchInput name={getProperty('referenceNumber')} ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:AP_Number')} />
              <SearchInput name={getProperty('arNumber')} ref={register} />
              <SearchLabel label={t('text:Currency')} />
              <SearchSelect
                selectOptions={getSelectOptions<CURRENCY_TYPE>('CURRENCY_TYPE', 'ALL', true)}
                name={getProperty('currencyType')}
                ref={register}
              />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Partner_Name')} />
              <SearchInput name={getProperty('dealerEnterpriseName')} ref={register} />
              <SearchLabel label={t('text:Partner_Code_of_Anchor')} />
              <SearchInput name={getProperty('dealerCodeByAnchor')} ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Disbursement_Amount')} />
              <SearchInput
                col={2}
                placeholder={t('text:from')}
                name={getProperty('minimumDisbursedAmount')}
                ref={register}
              />
              <SearchInput
                col={2}
                placeholder={t('text:to')}
                name={getProperty('maximumDisbursedAmount')}
                ref={register}
              />
              <SearchLabel label={t('text:Applied_Date')} />
              <SearchDatePicker
                placeholder={t('text:from')}
                name={getProperty('requestedDateFrom')}
                control={control}
              />
              <SearchDatePicker placeholder={t('text:to')} name={getProperty('requestedDateTo')} control={control} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Approved_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name={getProperty('approvedDateFrom')} control={control} />
              <SearchDatePicker placeholder={t('text:to')} name={getProperty('approvedDateTo')} control={control} />
              <SearchLabel label={t('text:Disbursed_Date')} />
              <SearchDatePicker
                placeholder={t('text:from')}
                name={getProperty('disbursedDateFrom')}
                control={control}
              />
              <SearchDatePicker placeholder={t('text:to')} name={getProperty('disbursedDateTo')} control={control} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Scheduled_Repayment_Date')} />
              <SearchDatePicker
                placeholder={t('text:from')}
                name={getProperty('repaymentDateFrom')}
                control={control}
              />
              <SearchDatePicker placeholder={t('text:to')} name={getProperty('repaymentDateTo')} control={control} />
              <SearchLabel label={t('text:Repaid_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name={getProperty('repaidDateFrom')} control={control} />
              <SearchDatePicker placeholder={t('text:to')} name={getProperty('repaidDateTo')} control={control} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Program_Type')} />
              <SearchSelect
                name="collateralType"
                selectOptions={getSelectOptions<COLLATERAL_TYPE>('COLLATERAL_TYPE', 'ALL', true)}
                ref={register}
              />
              <SearchEmpty />
            </div>
            <div className="row">
              <SearchLabel
                className="search-form__label-wrap auto-height col-2"
                label={t('text:Financing_Status')}
                hasStatusDescription={true}
                showStatusDescriptionFunc={onClickFinancingStatus}
              />
              <div className="search-form__item__input auto-height col-10">
                <div className="checkbox-form">
                  <div className="form-check-inline">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value=""
                      id="allCheck"
                      onChange={handleCheckAll}
                      checked={watchCheckbox?.length === NUM_OF_CHECKBOX}
                    />
                    <label className="form-check-label" htmlFor="allCheck">
                      {t('text:All')}
                    </label>
                  </div>
                  {LoanStatusCheckBoxOptions?.map((checkBoxValue, index) => {
                    return (
                      <div className="form-check-inline" key={index}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          value={checkBoxValue}
                          id={`flexCheckDefault${index}`}
                          name={getProperty('loanStatusConditions')}
                          ref={register}
                        />
                        <label className="form-check-label" htmlFor={`flexCheckDefault${index}`}>
                          {t(`code:financing-status.${LoanStatusCheckBoxOptions[index]}`)}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </SearchBorder>
          <div className="flex-center mt-4">
            <Button size={ButtonSizeEnum.LG} onClick={onSearchSubmit}>
              {t('text:Search')}
            </Button>
          </div>
        </form>
      </div>
      <div className="division-border" />
      <div className="content-area">
        <SectionTitle title={t('text:Result')} />
        <div className="d-flex mb-2">
          <p className="total-data me-auto">
            {t('text:Total')} {financingPage?.totalElements}
          </p>
          <Button onClick={onClickExportButton} variant={ButtonVariantEnum.OUTLINED}>
            {t('text:Export')}
          </Button>
        </div>
        <TableBorder>
          <TableHeader header={tableHeaders} />
          <TableBody numOfCol={tableHeaders.length}>{renderResultTable(financingPage?.content)}</TableBody>
        </TableBorder>
        <Pagination pageable={pageable} paginate={paginate} />
      </div>
    </>
  );
}

export default AnchorFinancingList;
